import { isEqual, remove, uniq, uniqWith } from 'lodash';
import React, { useCallback, useContext, useReducer } from 'react';
import { NotebookContext } from '../../context/NotebookContext';
import { UserContext } from '../../context/UserContext';
import client from '../../lib/client';
import redirect from '../../lib/redirect';
import { measurementsQuery } from '../../queries/measurements';
import { notebookQuery } from '../../queries/notebooks';
import { noteCreateOrUpdateMutation, notesQuery } from '../../queries/notes';
import { MeasurementsQueryVariables } from '../../queries/__generated__/MeasurementsQuery';
import {
  NoteCreateOrUpdateMutation,
  NoteCreateOrUpdateMutationVariables
} from '../../queries/__generated__/NoteCreateOrUpdateMutation';
import { NotesQueryVariables } from '../../queries/__generated__/NotesQuery';
import { Entry } from './entry';
import { ContextProps, EditorAction, EditorState, EntryProps } from './types';
import { back } from '../../lib/helpers/navigate';

const EditorContext = React.createContext({} as ContextProps);

interface EditorProviderProps {
  entry?: EntryProps;
}

const EditorProvider: React.FC<EditorProviderProps> = ({ children, entry }) => {
  const { user } = useContext(UserContext);
  const { notebook } = useContext(NotebookContext);
  const initialEditorState = {
    input: entry ? new Entry(entry) : Entry.newEntry(user.id),
    subjectDisplay: entry ? entry.subjects!.map(s => ({ subjectId: s.id, groupId: '' })) : [],
    showSubjectPicker: entry ? false : true,
    showDateModal: false
  };

  function editorReducer(state: EditorState, action: EditorAction) {
    switch (action.type) {
      case 'addSubject': {
        state.subjectDisplay.push({
          subjectId: action.subjectId,
          groupId: action.groupId ? action.groupId : ''
        });
        state.subjectDisplay = uniqWith(state.subjectDisplay, isEqual);
        state.showSubjectPicker = false;
        return { ...state };
      }

      case 'removeSubject': {
        remove(state.subjectDisplay, x => {
          return isEqual(x, {
            subjectId: action.subjectId,
            groupId: action.groupId ? action.groupId : ''
          });
        });
        return { ...state };
      }

      case 'updateCaption': {
        state.input.context = action.value;
        return { ...state };
      }

      case 'updateContent': {
        state.input.content = action.value;
        return { ...state };
      }

      case 'updateMeasurements': {
        const measurements = state.input.measurements;

        // Find matching measurement
        const measurement = measurements.filter(
          m => m.trackerId === action.trackerId && m.subjectId === action.subjectId
        );

        if (measurement.length > 0) {
          // Update measurement
          measurements.forEach((m, i: number) => {
            if (m.subjectId === action.subjectId && m.trackerId === action.trackerId) {
              if (measurements[i].value === action.value) {
                // Unset value if it is clicked when active
                measurements.splice(i, 1);
              } else {
                // Set value on tracker
                measurements[i] = { ...measurement[0], value: action.value };
              }
            }
          });
        } else {
          measurements.push({
            subjectId: action.subjectId,
            trackerId: action.trackerId,
            value: action.value
          });
        }

        return { ...state };
      }

      case 'closeSubjectPicker': {
        if (state.input.subjectIds.length < 1) {
          back();
          return state;
        }
        return { ...state, showSubjectPicker: false };
      }

      case 'openSubjectPicker': {
        return { ...state, showSubjectPicker: true };
      }

      case 'selectEntryDate': {
        state.input.notedAt = action.value;
        return { ...state };
      }

      case 'saveEntry': {
        state.input.subjectIds = uniq(state.subjectDisplay.map(item => item.subjectId));

        client
          .mutate<NoteCreateOrUpdateMutation, NoteCreateOrUpdateMutationVariables>({
            mutation: noteCreateOrUpdateMutation,
            variables: {
              input: state.input
            },
            refetchQueries: [
              {
                query: notesQuery,
                variables: {
                  input: { notebookId: notebook.id, first: 20 }
                } as NotesQueryVariables
              },
              {
                query: notebookQuery,
                variables: { id: notebook.id }
              },
              {
                query: measurementsQuery,
                variables: { input: { notebookId: notebook.id } } as MeasurementsQueryVariables
              }
            ]
          })
          .then(() => {
            redirect('/notebook');
          });
        return state;
      }

      default:
        return state;
    }
  }

  /*
   * Avoid breaking re-renders by memoizing the reducer.
   * Removing this will result in missed clicks when
   * taking a measurement. <3 Dan
   * https://stackoverflow.com/questions/55055793/react-usereducer-hook-fires-twice-how-to-pass-props-to-reducer
   */
  const memoizedEditorReducer = useCallback(editorReducer, []);
  const [state, dispatch] = useReducer(memoizedEditorReducer, initialEditorState);

  const value = { state, dispatch };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};

export { EditorContext, EditorProvider };
